import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, BookOpen, ExternalLink, ArrowRight, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { formatYMD } from "@/lib/dates";
import SEO from "@/components/SEO";

const SOARING_AMAZON_URL =
  "https://www.amazon.com/dp/B0GVPGD51L?&linkCode=sl2&tag=avianayoga0b-20&linkId=39754c5c9178a30c152d18c5703ffc2b&language=en_US&ref_=as_li_ss_tl";

/**
 * Studio-paid training registration confirmation.
 * Shown after submitting a registration for a training whose tuition is paid
 * by the hosting studio directly (e.g. ASNA Lincolnwood). The student does
 * NOT route through /payment/<id>; they land here instead.
 */
export default function TrainingRegistrationConfirmed() {
  const { regId } = useParams();
  const [reg, setReg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trainings/registrations/${regId}`);
        setReg(data);
      } catch {
        setError("We couldn't find that registration. If you believe this is a mistake, please email holly@avianayoga.com.");
      }
    })();
  }, [regId]);

  if (error) {
    return (
      <div className="pt-40 pb-24 container-aviana max-w-2xl text-center">
        <p className="text-aviana-plum">{error}</p>
      </div>
    );
  }
  if (!reg) {
    return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;
  }

  const firstName = (reg.full_name || "").split(" ")[0];
  const dateStr = reg.offering_start_date
    ? formatYMD(reg.offering_start_date, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="training-registration-confirmed">
      <SEO noindex title="Registration Confirmed" description="Your training registration is confirmed." />
      <div className="container-aviana max-w-2xl">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-aviana-gold/15 text-aviana-goldDeep mb-6">
            <CheckCircle2 size={32} strokeWidth={1.5} />
          </div>
          <p className="overline-plum mb-3">Registration confirmed</p>
          <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-tight">
            You're in, <em className="italic font-serif gold-text">{firstName}.</em>
          </h1>
          <p className="mt-5 font-serif italic text-aviana-muted text-[18px] leading-[1.6]">
            Your spot at <span className="text-aviana-ink not-italic font-medium">{reg.offering_title}</span> is held with intention.
            {dateStr && <> Holly will see you on <span className="text-aviana-ink not-italic font-medium">{dateStr}</span>.</>}
          </p>
        </div>

        {/* Tuition handled by studio */}
        <div className="bg-white border border-aviana-border rounded-[1.75rem] p-7 md:p-9 mb-6" data-testid="tuition-block">
          <p className="overline-plum mb-2">Tuition</p>
          <p className="font-figure text-3xl text-aviana-ink leading-tight">
            ${reg.offering_price_usd?.toLocaleString()} <span className="text-aviana-muted text-base font-normal italic">— handled by the studio</span>
          </p>
          <p className="mt-3 text-aviana-inkSoft text-[15px] leading-[1.7]">
            Your hosting studio is paying Holly directly. There's nothing to pay here on the website. You'll receive an email receipt confirming your registration shortly.
          </p>
        </div>

        {/* LMS access */}
        <div className="bg-aviana-plumDeep text-aviana-cream rounded-[1.75rem] p-7 md:p-9 mb-6 relative overflow-hidden" data-testid="lms-block">
          <div className="absolute inset-0 grain pointer-events-none opacity-50" aria-hidden />
          <div className="relative">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-full bg-aviana-gold/20 text-aviana-goldSoft flex items-center justify-center flex-shrink-0">
                <GraduationCap size={20} strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-goldSoft">Online training modules</p>
                <h2 className="font-display text-2xl md:text-3xl text-aviana-cream mt-1 leading-tight">Your portal is ready.</h2>
              </div>
            </div>
            <p className="text-aviana-cream/85 leading-[1.7] text-[15px]">
              Every in-person training registrant receives <strong className="text-aviana-cream">5 months of access</strong> to the <strong className="text-aviana-cream">Aerial Yoga Teacher Training Modules</strong> course — your portal for the exam, research paper, practice teaching log, and class audit submission. Your access begins the day the training starts. If you need more time, paid extensions are available in your dashboard (1 month $100 · 2 months $300 · 3 months $500).
            </p>
            <p className="mt-3 text-aviana-cream/85 leading-[1.7] text-[15px]">
              We've sent a separate email with your <strong className="text-aviana-cream">sign-in link and temporary password</strong>. Once you log in, please change your password under Dashboard → Settings.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-aviana-gold text-aviana-plumDeep text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-aviana-goldSoft transition-colors"
              data-testid="lms-sign-in-cta"
            >
              Sign in to your modules <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Required book — always shown for trainings (every student needs a copy) */}
        <div className="bg-aviana-cream/70 border border-aviana-plum/25 rounded-[1.75rem] p-7 md:p-9 mb-6" data-testid="book-block">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
              <BookOpen size={18} />
            </div>
            <div className="flex-1">
              <p className="overline-plum mb-2">Your required text</p>
              <p className="text-aviana-inkSoft text-[15px] leading-[1.65]">
                <em className="text-aviana-ink not-italic font-medium">Soaring with the Soul: An Aerial Yoga Guide</em> is Holly's training text and is required for every teacher training. {reg.add_book ? "You confirmed you already own a copy — see you with it in hand on day one." : "If you don't already own a copy, please order one now so you have it ready for the in-person dates."}
              </p>
              <a
                href={SOARING_AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-aviana-plum text-aviana-cream text-[12px] tracking-[0.18em] uppercase hover:bg-aviana-plumDeep transition-colors"
                data-testid="book-amazon-link"
              >
                {reg.add_book ? "Order another copy" : "Purchase on Amazon"} <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>

        {/* What's next */}
        <div className="text-center mt-12">
          <p className="text-aviana-muted text-[14px] leading-[1.7]">
            Questions about your training? Email{" "}
            <a href="mailto:holly@avianayoga.com" className="text-aviana-plum hover:text-aviana-goldDeep underline underline-offset-2">
              holly@avianayoga.com
            </a>
            .
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink"
            data-testid="back-home-link"
          >
            Return home →
          </Link>
        </div>
      </div>
    </div>
  );
}
